import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Package, Loader2, AlertCircle, Gem, Edit3 } from 'lucide-react'
import useCasesStore from '../store/useCasesStore'
import useAppStore from '../store/useAppStore'
import { formatPrice } from '../utils/formatters'
import toast from 'react-hot-toast'
import SeedEditor, { resetSavedSeed } from '../components/SeedEditor'
import AnimatedButton from '../components/AnimatedButton'
import useAudioManager from '../hooks/useAudioManager'

const CaseDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { currentCase, loadCaseDetails, isLoading, error } = useCasesStore()
  const { balance } = useAppStore()
  
  // Инициализация Audio Manager
  const { playSound } = useAudioManager({
    pick: 'pick'
  })
  
  // Состояние для модалки редактора seed
  const [isSeedEditorOpen, setIsSeedEditorOpen] = useState(false)
  // Состояние загрузки изображения кейса
  const [isImageLoading, setIsImageLoading] = useState(true)
  // Состояние количества открытий кейса (1-10)
  const [openCount, setOpenCount] = useState(1)

  useEffect(() => {
    // Load case details from API
    loadCaseDetails(id)
    // Сбрасываем состояние загрузки изображения при смене кейса
    setIsImageLoading(true)
  }, [id, loadCaseDetails])

  // Use currentCase from API response for all case data
  const caseItem = currentCase

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-primary text-white flex items-center justify-center">
        <Loader2 className="animate-spin text-accent-primary" size={48} />
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-dark-primary text-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={64} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">
            {t('common.error')}
          </h2>
          <p className="text-text-secondary mb-4">{error}</p>
          <button 
            onClick={() => loadCaseDetails(id)}
            className="bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary px-6 py-2 rounded-lg font-semibold hover:opacity-90 transition-all"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    )
  }

  // Case not found - only show this if not loading and no currentCase
  if (!isLoading && !caseItem) {
    return (
      <div className="min-h-screen bg-dark-primary text-white flex items-center justify-center">
        <div className="text-center">
          <Package size={64} className="mx-auto text-text-secondary mb-4" />
          <h2 className="text-xl font-semibold text-text-secondary">
            {t('cases.caseNotFound')}
          </h2>
        </div>
      </div>
    )
  }

  // Don't render main content if still loading or no case data
  if (isLoading || !caseItem) {
    return null
  }

  const handleOpenCase = async () => {
    // Отправляем цель в Яндекс.Метрику
    if (window.ym) {
      window.ym(104258304, 'reachGoal', 'open-case')
    }
    
    // Рассчитываем общую стоимость
    const totalCost = caseItem.price * openCount
    
    if (balance >= totalCost) {
      // Сбрасываем сохраненный токен перед открытием кейса
      resetSavedSeed()
      
      // Если открываем 1 кейс - переходим на обычную страницу с рулеткой
      if (openCount === 1) {
        navigate(`/cases/${id}/opening`, { 
          state: { allowOpening: true } 
        })
      } else {
        // Если открываем 2+ кейса - переходим на страницу множественного открытия
        navigate(`/cases/${id}/opening-multi`, { 
          state: { allowOpening: true, count: openCount } 
        })
      }
    } else {
      navigate('/payment')
    }
  }
  
  // Функция увеличения количества открытий
  const incrementCount = () => {
    if (openCount < 10) {
      playSound('pick')
      setOpenCount(openCount + 1)
    }
  }
  
  // Функция уменьшения количества открытий
  const decrementCount = () => {
    if (openCount > 1) {
      playSound('pick')
      setOpenCount(openCount - 1)
    }
  }

  // Get skins from currentCase (API response) or empty array
  const skins = currentCase?.skins || []
  
  // Сортируем скины по баллу цены (от большего к меньшему)
  const sortedSkins = [...skins].sort((a, b) => (b.price || 0) - (a.price || 0))
  
  // Проверка на элитный предмет (балл 9 или 10)
  const isEliteItem = (item) => {
    return item?.price >= 9
  }

  // Функция для сокращения состояния износа
  const shortenWear = (text) => {
    return text
      .replace(/\(Factory New\)/gi, 'FN')
      .replace(/\(Minimal Wear\)/gi, 'MW')
      .replace(/\(Field-Tested\)/gi, 'FT')
      .replace(/\(Well-Worn\)/gi, 'WW')
      .replace(/\(Battle-Scarred\)/gi, 'BS')
  }

  const controlsPanelContent = (
    <>
      <div className="flex items-center justify-center gap-3">
        <button
          onClick={decrementCount}
          disabled={openCount === 1}
          className={`w-10 h-10 rounded-lg font-bold text-xl transition-all ${
            openCount === 1
              ? 'bg-surface text-text-secondary/50 cursor-not-allowed'
              : 'bg-surface text-white hover:bg-surface/80 active:scale-95'
          }`}
        >
          −
        </button>

        <div className="flex items-center justify-center min-w-[100px] h-10 bg-gradient-to-r from-accent-primary/20 to-accent-secondary/20 border-2 border-accent-primary/50 rounded-lg">
          <span className="text-xl font-bold text-accent-primary">{openCount}</span>
        </div>

        <button
          onClick={incrementCount}
          disabled={openCount === 10}
          className={`w-10 h-10 rounded-lg font-bold text-xl transition-all ${
            openCount === 10
              ? 'bg-surface text-text-secondary/50 cursor-not-allowed'
              : 'bg-surface text-white hover:bg-surface/80 active:scale-95'
          }`}
        >
          +
        </button>
      </div>

      <div>
        {balance >= (caseItem.price * openCount) ? (
          <AnimatedButton onClick={handleOpenCase}>
            <span className="flex items-center justify-center gap-2">
              {openCount === 1
                ? t('cases.openFor', { price: Number(caseItem.price).toLocaleString() })
                : t('cases.openCountFor', { count: openCount, price: Number(caseItem.price * openCount).toLocaleString() })
              }
              <Gem size={20} />
            </span>
          </AnimatedButton>
        ) : (
          <button
            onClick={handleOpenCase}
            className="w-full py-4 rounded-lg font-bold text-lg transition-all shadow-lg flex items-center justify-center gap-2 hover:opacity-90 transform hover:scale-[1.02] active:scale-[0.98] bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-blue-500/30"
          >
            {t('cases.topUpOn', { amount: Number((caseItem.price * openCount) - balance).toLocaleString() })}
            <Gem size={20} />
          </button>
        )}
      </div>

      <div className="text-center">
        <button
          onClick={() => {
            console.log('Opening seed editor...')
            setIsSeedEditorOpen(true)
          }}
          className="text-text-secondary hover:text-accent-primary transition-colors inline-flex items-center gap-2 text-sm"
        >
          <Edit3 size={14} />
          {t('seedEditor.editSeed')}
        </button>
      </div>
    </>
  )

  return (
    <div className="min-h-screen bg-dark-primary text-white pb-[100px] lg:pb-0">
      {/* Header */}
      <div className="p-6 pb-0">
        <div className="flex items-center gap-4 mb-4 justify-center">
          <button 
            onClick={() => navigate('/cases')}
            className="p-2 rounded-lg bg-surface hover:bg-opacity-80 transition-all"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold text-accent-primary lg:text-4xl">
            {caseItem.name}
          </h1>
        </div>
        
      </div>

      {/* Case Preview */}
      <div className="lg:mx-auto lg:w-full lg:max-w-md">
        {/* Large Case Image - на всю ширину */}
        <div className="relative mb-6 w-full bg-gradient-to-br from-dark-primary to-surface lg:overflow-hidden lg:rounded-[32px] lg:border lg:border-white/10 lg:bg-gradient-to-br lg:from-surface/80 lg:via-dark-primary lg:to-surface/70 lg:p-5 lg:shadow-[0_24px_80px_rgba(0,0,0,0.45)]">
          <div className="pointer-events-none absolute inset-x-10 top-4 hidden h-24 rounded-full bg-accent-primary/10 blur-3xl lg:block" />
          <div className="pointer-events-none absolute inset-x-16 bottom-0 hidden h-20 rounded-full bg-white/5 blur-3xl lg:block" />
          {/* Скелетон с фиксированным aspect ratio */}
          {isImageLoading && (
            <div className="aspect-square w-full bg-black animate-pulse lg:rounded-2xl" />
          )}
          <img 
            src={caseItem.img} 
            alt={caseItem.name}
            className={`w-full h-auto object-cover lg:rounded-2xl lg:border lg:border-white/10 lg:bg-black/20 lg:shadow-[0_20px_50px_rgba(0,0,0,0.35)] ${isImageLoading ? 'absolute inset-0 opacity-0' : 'opacity-100'} `}
            onLoad={() => setIsImageLoading(false)}
            onError={() => setIsImageLoading(false)}
          />
        </div>
        
        {/* Case Info */}
        <div className="px-6 mb-6 lg:px-0">
          <p className="text-center text-white/90 text-base leading-relaxed">
            {caseItem.desc}
          </p>
        </div>

        <div className="hidden lg:block lg:mb-8">
          <div className="space-y-3">
            {controlsPanelContent}
          </div>
        </div>
      </div>

      <div className="px-6">
        {/* Skins List */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-white mb-4">{t('cases.possibleItems')}</h3>
          <div className="grid grid-cols-3 gap-3 lg:grid-cols-6">
            {sortedSkins.map((skin, index) => {
              const isElite = isEliteItem(skin)
              return (
                <div 
                  key={index}
                  className={`rounded-lg p-2 border relative flex flex-col ${
                    isElite
                      ? 'border-yellow-500/20 bg-gradient-to-r from-yellow-500/15 via-yellow-400/10 to-yellow-500/5'
                      : 'bg-surface border-white border-opacity-10'
                  }`}
                >
                  {/* Тонкое элитное свечение */}
                  {isElite && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-400/8 to-transparent animate-pulse rounded-lg"></div>
                  )}
                  
                  <div className="aspect-square rounded mb-2 overflow-hidden relative z-10 flex items-center justify-center p-2">
                    <img 
                      src={skin.img} 
                      alt={skin.name}
                      className={`w-auto h-auto max-w-full max-h-full object-contain ${
                        isElite ? 'drop-shadow-[0_0_6px_rgba(250,204,21,0.4)]' : ''
                      }`}
                    />
                  </div>
                  
                  {/* Название скина */}
                  <div className="text-sm font-medium text-center leading-tight relative z-10 flex-grow">
                    {skin.name.includes('|') ? (
                      <>
                        <div className="text-white">
                          {skin.name.split('|')[0].trim()}
                        </div>
                        <div className="text-text-secondary text-xs mt-1">
                          {shortenWear(skin.name.split('|')[1].trim())}
                        </div>
                      </>
                    ) : (
                      <div className="text-white">
                        {shortenWear(skin.name)}
                      </div>
                    )}
                  </div>
                  
                  {/* Множитель - прижат к низу */}
                  {skin.x !== undefined && (
                    <div className="mt-2 text-center relative z-10">
                      <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                        skin.x < 1
                          ? 'bg-gray-600 text-gray-300'
                          : 'bg-accent-primary/20 text-accent-primary border border-accent-primary/30'
                      }`}>
                        x{skin.x % 1 === 0 ? skin.x : skin.x.toFixed(1)}
                      </span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Фиксированная панель управления внизу */}
      <div className="fixed bottom-0 left-0 right-0 bg-dark-primary border-t border-white/10 z-50 lg:hidden">
        <div className="px-6 py-4 space-y-3">
          {controlsPanelContent}
        </div>
      </div>

      {/* Модальное окно редактора seed */}
      <SeedEditor 
        isOpen={isSeedEditorOpen} 
        onClose={() => setIsSeedEditorOpen(false)} 
      />
    </div>
  )
}

export default CaseDetail
